"use client";

import { useEffect, useRef } from "react";
import { usePathname } from "next/navigation";

const BUILD_ID = process.env.NEXT_PUBLIC_BUILD_ID;
const CHECK_THROTTLE_MS = 60 * 1000;

// Home-screen installs and backgrounded mobile tabs resume a suspended page
// instead of reloading, so a deploy leaves them running stale JS until a
// chunk request 404s (Colossal's Samsung Browser tab, 26 Oct 2026). Runs for
// every tab; the boundaries' chunk-error reload is the fallback.
//
// Reloading the moment the app returned to the foreground threw away whatever
// was being typed: Safari Taxidermy lost thirty mounts in Edit Job to a deploy
// (support ticket #2). A new build is now noted on foreground and applied on
// the next route change, when nothing in progress can be lost.
export const useReloadOnNewBuild = () => {
  const pathname = usePathname();
  const reloadPending = useRef(false);
  const lastCheck = useRef(0);

  useEffect(() => {
    if (process.env.NODE_ENV !== "production" || !BUILD_ID) return;

    const check = async () => {
      if (reloadPending.current) return;
      if (Date.now() - lastCheck.current < CHECK_THROTTLE_MS) return;
      lastCheck.current = Date.now();
      try {
        const res = await fetch("/api/version", { cache: "no-store" });
        const { buildId } = await res.json();
        if (buildId && buildId !== BUILD_ID) reloadPending.current = true;
      } catch {
        // Offline or flaky network: stale is better than a failed reload loop
      }
    };

    const onVisible = () => {
      if (document.visibilityState === "visible") check();
    };
    document.addEventListener("visibilitychange", onVisible);
    // bfcache restores in regular Safari tabs skip visibilitychange
    window.addEventListener("pageshow", onVisible);
    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("pageshow", onVisible);
    };
  }, []);

  // pathname only changes on a real navigation, so the render that set the
  // flag never reloads; the next one the user makes does.
  useEffect(() => {
    if (reloadPending.current) window.location.reload();
  }, [pathname]);
};
